import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { IActivity, KeyStage, TextDetectiveConfig } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import {
    StageCard,
    TrafficLightIndicator,
    TrafficLightStatus,
} from './primitives';
import { AudioPromptButton } from './AudioPromptButton';
import { Search, AlertCircle, Check } from 'lucide-react-native';

interface TextDetectiveActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

export const TextDetectiveActivity: React.FC<TextDetectiveActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const config = (activity.config || {}) as TextDetectiveConfig;
    const errors = useMemo(() => config.errors || [], [config.errors]);
    const passage = config.passage || '';

    // Map of corrected error IDs to the selected replacement text
    const [correctedErrors, setCorrectedErrors] = useState<Record<string, string>>({});
    // Currently active inspected error
    const [selectedErrorId, setSelectedErrorId] = useState<string | null>(null);

    const [trafficStatus, setTrafficStatus] = useState<TrafficLightStatus>('idle');
    const [statusMessage, setStatusMessage] = useState<string>(
        config.prompt || 'Find and tap the hidden spelling and grammar errors in the passage.'
    );
    const [hasCompleted, setHasCompleted] = useState(false);

    const totalErrors = errors.length;
    const foundCount = Object.keys(correctedErrors).length;

    // Tokens split for interactive tapping
    const words = useMemo(() => {
        return passage.split(/\s+/).filter(Boolean);
    }, [passage]);

    const activeError = useMemo(() => {
        return errors.find((e) => e.id === selectedErrorId) || null;
    }, [errors, selectedErrorId]);

    const handleWordPress = (word: string) => {
        if (hasCompleted) return;
        const cleaned = word.replace(/[^a-zA-Z']/g, '').toLowerCase();

        // Check if cleaned matches any target error
        const matched = errors.find(
            (e) => e.targetText.toLowerCase() === cleaned || e.targetText.toLowerCase() === word.toLowerCase()
        );

        if (matched) {
            setSelectedErrorId(matched.id);
            setTrafficStatus('idle');
            setStatusMessage(`Clue found! How should "${matched.targetText}" be corrected?`);
        } else {
            setStatusMessage(`"${word}" is grammatically correct. Keep looking!`);
        }
    };

    const handleSelectCorrection = (errorId: string, option: string) => {
        if (hasCompleted) return;
        const updated = { ...correctedErrors, [errorId]: option };
        setCorrectedErrors(updated);
        setSelectedErrorId(null);

        const newCount = Object.keys(updated).length;
        if (newCount === totalErrors && totalErrors > 0) {
            setTrafficStatus('correct');
            setStatusMessage(`Case Closed! You found and corrected all ${totalErrors} errors!`);
            setHasCompleted(true);

            useStrokeStore.getState().addCustomAnswer({
                activityId: activity.id,
                value: updated,
                mechanic: 'text_detective',
                isCorrect: true,
                score: 100,
            });

            setTimeout(() => {
                onComplete();
            }, 1200);
        } else {
            setTrafficStatus('idle');
            setStatusMessage(`Corrected! Found ${newCount} of ${totalErrors} errors.`);
        }
    };

    return (
        <StageCard testID="text-detective-card">
            <View style={styles.container}>
                {/* Header Row */}
                <View style={styles.headerRow}>
                    <View style={styles.titleArea}>
                        <View style={styles.badge}>
                            <Search size={14} color="#4F46E5" />
                            <Text style={styles.badgeText}>The Proofreader Detective</Text>
                        </View>
                        <Text style={styles.promptText}>{config.prompt || 'Spot the errors in the story.'}</Text>
                    </View>
                    <View style={styles.headerControls}>
                        {passage ? (
                            <AudioPromptButton
                                promptText={passage}
                                activityId={activity.id}
                                testID="text-detective-audio-button"
                            />
                        ) : null}
                        <View style={styles.counterBadge}>
                            <Text style={styles.counterText}>
                                {foundCount} / {totalErrors} Found
                            </Text>
                        </View>
                        <TrafficLightIndicator status={trafficStatus} size={28} />
                    </View>
                </View>

                {/* Story Passage Card */}
                <View style={styles.passageContainer}>
                    <Text style={styles.instructionsNotice}>Tap any word in the text that looks incorrect:</Text>
                    <View style={styles.wordsWrapper}>
                        {words.map((w, idx) => {
                            const cleaned = w.replace(/[^a-zA-Z']/g, '').toLowerCase();
                            const matchingError = errors.find(
                                (e) => e.targetText.toLowerCase() === cleaned || e.targetText.toLowerCase() === w.toLowerCase()
                            );
                            const isCorrected = matchingError && correctedErrors[matchingError.id];
                            const isInspecting = matchingError && matchingError.id === selectedErrorId;

                            return (
                                <TouchableOpacity
                                    key={`word_${idx}_${w}`}
                                    style={[
                                        styles.wordPill,
                                        isInspecting && styles.wordPillInspecting,
                                        isCorrected && styles.wordPillCorrected,
                                    ]}
                                    onPress={() => handleWordPress(w)}
                                    disabled={hasCompleted || !!isCorrected}
                                    accessibilityLabel={`Inspect word ${w}`}
                                    testID={`word-token-${idx}`}
                                >
                                    <Text
                                        style={[
                                            styles.wordText,
                                            isCorrected && styles.wordTextCorrected,
                                            isInspecting && styles.wordTextInspecting,
                                        ]}
                                    >
                                        {isCorrected ? matchingError?.correctText : w}
                                    </Text>
                                    {isCorrected && <Check size={12} color="#15803D" style={styles.checkIcon} />}
                                </TouchableOpacity>
                            );
                        })}
                    </View>
                </View>

                {/* Status Bar */}
                <View style={styles.statusBar}>
                    <Text style={styles.statusText}>{statusMessage}</Text>
                </View>

                {/* Active Error Inspector Sheet */}
                {activeError && (
                    <View style={styles.inspectorContainer} testID="error-inspector-sheet">
                        <View style={styles.inspectorHeader}>
                            <AlertCircle size={18} color="#D97706" />
                            <Text style={styles.inspectorTitle}>
                                Error Detected: "{activeError.targetText}" ({activeError.errorType.toUpperCase()})
                            </Text>
                        </View>
                        <Text style={styles.inspectorExplanation}>{activeError.explanation}</Text>

                        <Text style={styles.optionsPrompt}>Choose the correct form:</Text>
                        <View style={styles.optionsRow}>
                            {(activeError.options || [activeError.correctText]).map((opt) => (
                                <TouchableOpacity
                                    key={opt}
                                    style={styles.optionButton}
                                    onPress={() => handleSelectCorrection(activeError.id, opt)}
                                    accessibilityLabel={`Replace with ${opt}`}
                                    testID={`fix-option-${opt}`}
                                >
                                    <Text style={styles.optionButtonText}>{opt}</Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                    </View>
                )}
            </View>
        </StageCard>
    );
};

const styles = StyleSheet.create({
    container: {
        padding: 16,
        gap: 12,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        paddingBottom: 10,
    },
    titleArea: {
        flex: 1,
        paddingRight: 12,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 8,
        paddingVertical: 3,
        backgroundColor: '#EEF2FF',
        borderRadius: 12,
        alignSelf: 'flex-start',
        marginBottom: 6,
    },
    badgeText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#4F46E5',
    },
    promptText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1E293B',
    },
    headerControls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    counterBadge: {
        backgroundColor: '#1E293B',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
    },
    counterText: {
        fontSize: 13,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    passageContainer: {
        backgroundColor: '#F8FAFC',
        padding: 16,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        gap: 10,
    },
    instructionsNotice: {
        fontSize: 12,
        fontWeight: '800',
        color: '#64748B',
        textTransform: 'uppercase',
    },
    wordsWrapper: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'center',
    },
    wordPill: {
        paddingVertical: 4,
        paddingHorizontal: 6,
        borderRadius: 6,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
    },
    wordPillInspecting: {
        borderColor: '#F59E0B',
        backgroundColor: '#FEF3C7',
    },
    wordPillCorrected: {
        borderColor: '#86EFAC',
        backgroundColor: '#DCFCE7',
    },
    wordText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1E293B',
    },
    wordTextInspecting: {
        color: '#B45309',
        fontWeight: '800',
    },
    wordTextCorrected: {
        color: '#15803D',
        fontWeight: '800',
        textDecorationLine: 'none',
    },
    checkIcon: {
        marginLeft: 2,
    },
    statusBar: {
        backgroundColor: '#F1F5F9',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
    },
    statusText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#475569',
        textAlign: 'center',
    },
    inspectorContainer: {
        backgroundColor: '#FFFBEB',
        borderRadius: 12,
        borderWidth: 2,
        borderColor: '#FDE68A',
        padding: 14,
        gap: 8,
    },
    inspectorHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    inspectorTitle: {
        fontSize: 14,
        fontWeight: '800',
        color: '#B45309',
    },
    inspectorExplanation: {
        fontSize: 14,
        fontWeight: '600',
        color: '#451A03',
        lineHeight: 20,
    },
    optionsPrompt: {
        fontSize: 13,
        fontWeight: '800',
        color: '#78350F',
        marginTop: 4,
    },
    optionsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    optionButton: {
        backgroundColor: '#FFFFFF',
        borderWidth: 2,
        borderColor: '#F59E0B',
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 10,
    },
    optionButtonText: {
        fontSize: 15,
        fontWeight: '800',
        color: '#B45309',
    },
});
